import { APP_BASE } from '../config.js';

// Utility function to get correct image/upload URL for both dev and production
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;

  // Remove leading slashes but preserve the path structure
  const cleanPath = imagePath.replace(/^\/+/, '');

  // In local dev: use full URL since proxy doesn't handle /uploads/
  // In production: use /crf/ prefix
  const isLocalDev = typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

  // If path already starts with 'uploads/', use it as-is
  // Otherwise, assume it's a relative path that needs uploads/ prefix
  if (cleanPath.startsWith('uploads/')) {
    const base = isLocalDev ? "http://localhost:5000/" : "/crf/";
    return `${base}${cleanPath}`;
  } else {
    // Path doesn't start with uploads/, add it
    const base = isLocalDev ? "http://localhost:5000/uploads/" : "/crf/uploads/";
    return `${base}${cleanPath}`;
  }
};

// Utility function to get correct file URL for both dev and production
export const getFileUrl = (filePath) => {
  if (!filePath) return null;
  if (filePath.startsWith('http')) return filePath;
  const cleanPath = filePath.replace(/^\/+/, '');
  // In local dev: use full URL since proxy doesn't handle /uploads/
  // In production: use /crf/uploads/ or /crf/ based on path
  const isLocalDev = typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
  // Check if it's an upload path
  if (cleanPath.startsWith('uploads/')) {
    const uploadBase = isLocalDev ? "http://localhost:5000/" : "/crf/";
    return `${uploadBase}${cleanPath}`;
  }
  // For other paths, use API_BASED_URL logic
  const { API_BASED_URL } = require('../config.js');
  return `${API_BASED_URL}${cleanPath}`;
};

// Input sanitization
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  return input.trim().replace(/[<>]/g, '');
};

// Password validation
export const validatePassword = (password) => {
  const minLength = 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  return {
    isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar,
    errors: [
      password.length < minLength && 'Password must be at least 8 characters long',
      !hasUpperCase && 'Password must contain at least one uppercase letter',
      !hasLowerCase && 'Password must contain at least one lowercase letter',
      !hasNumbers && 'Password must contain at least one number',
      !hasSpecialChar && 'Password must contain at least one special character'
    ].filter(Boolean)
  };
};

// Email validation
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Phone number validation
export const validatePhone = (phone) => {
  const phoneRegex = /^[0-9]{10}$/;
  return phoneRegex.test(phone);
};

// File validation
export const validateFile = (file, options = {}) => {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'],
    maxFiles = 1
  } = options;

  if (!file) return { isValid: false, error: 'No file selected' };
  if (file.size > maxSize) return { isValid: false, error: 'File size exceeds limit' };
  if (!allowedTypes.includes(file.type)) return { isValid: false, error: 'Invalid file type' };

  return { isValid: true };
};

// XSS prevention for HTML content
export const escapeHtml = (unsafe) => {
  if (unsafe == null) return '';
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

// Token management
export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

export const setAuthToken = (token) => {
  localStorage.setItem('authToken', token);
};

export const removeAuthToken = () => {
  localStorage.removeItem('authToken');
};

// Admin token management
export const getAdminToken = () => {
  return localStorage.getItem('userToken');
};

export const setAdminToken = (token) => {
  localStorage.setItem('userToken', token);
};

export const removeAdminToken = () => {
  localStorage.removeItem('userToken');
  localStorage.removeItem('userPosition');
  localStorage.removeItem('userEmail');
};

// Decode JWT without verifying (client-side) to read exp
const decodeJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (_) {
    return null;
  }
};

let adminExpiryTimer = null;

export const startAdminTokenWatcher = () => {
  if (adminExpiryTimer) {
    clearTimeout(adminExpiryTimer);
    adminExpiryTimer = null;
  }
  const token = getAdminToken();
  if (!token) return;
  const payload = decodeJwt(token);
  if (!payload || !payload.exp) return;
  const msUntilExpiry = payload.exp * 1000 - Date.now();
  if (msUntilExpiry <= 0) {
    removeAdminToken();
    window.location.href = `${APP_BASE}/admin`;
    return;
  }
  adminExpiryTimer = setTimeout(() => {
    removeAdminToken();
    window.location.href = `${APP_BASE}/admin`;
  }, msUntilExpiry + 100); // small buffer
};

// Admin API request wrapper with security headers
export const secureAdminFetch = async (url, options = {}) => {
  const token = getAdminToken();
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  };

  if (token) {
    defaultHeaders['Authorization'] = token;
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers
    }
  });

  if (response.status === 401 || response.status === 403) {
    removeAdminToken();
    window.location.href = `${APP_BASE}/admin`;
    throw new Error('Admin session expired');
  }

  return response;
};

// API request wrapper with security headers
export const secureFetch = async (url, options = {}) => {
  const token = getAuthToken();
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  };

  if (token) {
    defaultHeaders['Authorization'] = token;
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers
    }
  });

  if (response.status === 401) {
    removeAuthToken();
    window.location.href = `${APP_BASE}/login`;
    throw new Error('Session expired');
  }

  return response;
};

// Rate limiting helper
export const createRateLimiter = (maxAttempts, windowMs) => {
  const attempts = new Map();

  return {
    check: (key) => {
      const now = Date.now();
      const userAttempts = attempts.get(key) || [];

      // Remove expired attempts
      const validAttempts = userAttempts.filter(time => now - time < windowMs);

      if (validAttempts.length >= maxAttempts) {
        return false;
      }

      validAttempts.push(now);
      attempts.set(key, validAttempts);
      return true;
    },

    reset: (key) => {
      attempts.delete(key);
    }
  };
}; 